import React, { useState, useRef, isValidElement, useEffect } from "react";
import ChildOrNull from "~/common/ChildOrNull";

interface Props {
  children?: React.ReactNode;
  slideRight: (props: {
    onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  }) => React.ReactNode;
  slideLeft: (props: {
    onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  }) => React.ReactNode;
  listWrapperProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLUListElement>,
    HTMLUListElement
  >;
  centeredIndex?: number;
}

type SliderProps = {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  render: (props: {
    onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  }) => React.ReactNode;
};

const SlideRight = ({ onClick, render }: SliderProps) => {
  return <>{render({ onClick })}</>;
};

const SlideLeft = ({ render, onClick }: SliderProps) => {
  return <>{render({ onClick })}</>;
};

export default function BrandCarousel({
  children,
  slideRight,
  slideLeft,
  listWrapperProps,
  centeredIndex,
}: Props) {
  const sliderRef = useRef<HTMLUListElement>(null);
  const [canSlideRight, setCanSlideRight] = useState<boolean>(false);
  const [canSlideLeft, setCanSlideLeft] = useState<boolean>(false);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

  const handleSlide = (direction: string) => {
    if (sliderRef.current) {
      const offset = Math.round(sliderRef.current.clientWidth * 0.8);
      sliderRef.current.scrollLeft -= direction === "left" ? offset : -offset;
    }
  };

  useEffect(function iniCarouselIntersectionObserver() {
    if (!sliderRef.current) {
      throw new Error(
        "sliderRef has not been set to the carousel <ul> element",
      );
    }
    const children = Array.from(sliderRef.current.children);
    const firstChild = children.shift();
    if (!firstChild) return;
    const lastChild = children.pop() ?? firstChild;
    const opts: IntersectionObserverInit = {
      root: sliderRef.current,
      threshold: [0, 0.1, 0.9, 1],
    };
    function onIntersection(entries: IntersectionObserverEntry[]) {
      entries.forEach((entry) => {
        const isInteresting = entry.intersectionRatio < 0.9;
        if (entry.target === firstChild) {
          setCanSlideRight(isInteresting);
        } else {
          setCanSlideLeft(isInteresting);
        }
      });
    }
    const observer = new IntersectionObserver(onIntersection, opts);

    observer.observe(firstChild);
    observer.observe(lastChild);

    return function cleanupCarouselIntersectionObserver() {
      observer.disconnect();
    };
  }, []);

  /** Allows us to remove the right border on the last carousel item when overflowing */
  useEffect(function initCarouselResizeObserver() {
    if (!sliderRef.current) {
      throw new Error(
        "sliderRef has not been set to the carousel <ul> element",
      );
    }
    function handleResize() {
      if (!sliderRef.current) {
        throw new Error(
          "sliderRef has not been set to the carousel <ul> element",
        );
      }
      setIsOverflowing(
        sliderRef.current.scrollWidth > sliderRef.current.clientWidth,
      );
    }
    const observer = new ResizeObserver(handleResize);
    observer.observe(sliderRef.current);

    return function cleanupCarouselResizeObserver() {
      observer.disconnect();
    };
  }, []);

  /** Allows us to center the active thumbnail when selected */
  useEffect(() => {
    if (centeredIndex && sliderRef.current) {
      const childToFocus = sliderRef.current.children[centeredIndex];

      if (!childToFocus) {
        return;
      }

      const parent = sliderRef.current;
      const parentRect = parent.getBoundingClientRect();
      const childRect = childToFocus.getBoundingClientRect();

      const scrollPosition =
        childRect.left -
        parentRect.left +
        parent.scrollLeft -
        parentRect.width / 2 +
        childRect.width / 2;

      parent.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [centeredIndex]);

  return (
    <>
      <ChildOrNull condition={canSlideRight}>
        <SlideLeft render={slideLeft} onClick={() => handleSlide("left")} />
      </ChildOrNull>
      <ul
        {...listWrapperProps}
        data-testid="brand-carousel"
        className={`${listWrapperProps?.className} scrollbar-width-none overflow-x-auto scroll-snap-x scroll-smooth px-2`}
        ref={sliderRef}
      >
        {React.Children.map(children, (child) =>
          isValidElement(child)
            ? React.createElement(child.type, { ...child.props, isOverflowing })
            : child,
        )}
      </ul>
      <ChildOrNull condition={canSlideLeft}>
        <SlideRight render={slideRight} onClick={() => handleSlide("right")} />
      </ChildOrNull>
    </>
  );
}
